import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import { Checkbox } from '@/shared/ui/checkbox/Checkbox';
import { Tooltip } from '@/shared/ui/tooltip/Tooltip';
import { Text } from '../../../text';
import type { CascaderOption } from '../helpers';

/**
 * カスケーダーオプションアイテムのプロパティ
 */
type CascaderOptionItemProps = {
  /** オプションデータ */
  option: CascaderOption;
  /** 階層レベル */
  level: number;
  /** アクティブ状態かどうか */
  isActive: boolean;
  /** 選択済みかどうか */
  isSelected: boolean;
  /** ホバー中かどうか */
  isHovered: boolean;
  /** 複数選択モードかどうか */
  multiple: boolean;
  /** 末端ノードのみ選択可能かどうか */
  onlyLeafSelectable: boolean;
  /** チェックボックストグル時のコールバック */
  onCheckboxToggle: () => void;
  /** ナビゲーション時のコールバック */
  onNavigate: () => void;
  /** マウスエンター時のコールバック */
  onMouseEnter: () => void;
};

/**
 * カスケーダーの個別オプションアイテムコンポーネント
 *
 * カスケーダー内の1つのオプション項目を表示します。
 * 単一選択モードではラベルのみ、複数選択モードではチェックボックスとラベルを表示します。
 *
 * @param props - CascaderOptionItemProps
 * @returns JSX.Element
 */
export const CascaderOptionItem = ({
  option,
  isActive,
  isSelected,
  isHovered,
  multiple,
  onlyLeafSelectable,
  onCheckboxToggle,
  onNavigate,
  onMouseEnter,
}: CascaderOptionItemProps) => {
  // 子要素を持つかどうか（中間階層かどうか）
  const hasChildren = option.children && option.children.length > 0;
  // チェックボックスを表示するかどうか
  // onlyLeafSelectable=true の場合、末端ノード（リーフ）のみ表示
  const showCheckbox = !onlyLeafSelectable || !hasChildren;
  const content = (
    // biome-ignore lint/a11y/useFocusableInteractive: Interactive children handle focus
    <div
      role="menuitem"
      onMouseEnter={onMouseEnter}
      className={cn(
        'flex w-full items-center justify-between',
        'text-sm text-text-base',
        'rounded-sm px-3 py-2',
        isHovered && 'bg-neutral-100',
        isActive && 'bg-brand-100',
      )}
    >
      {multiple ? (
        <>
          {/* チェックボックス: 選択用（onlyLeafSelectable=trueの場合は末端ノードのみ表示） */}
          {showCheckbox && (
            // biome-ignore lint/a11y/useSemanticElements: Using div with Checkbox component for accessibility
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (!option.disabled) {
                  onCheckboxToggle();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!option.disabled) {
                    onCheckboxToggle();
                  }
                }
              }}
              className={cn('flex shrink-0 items-center', !option.disabled && 'cursor-pointer')}
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={option.disabled}
              tabIndex={option.disabled ? -1 : 0}
            >
              <Checkbox
                checked={isSelected}
                className="pointer-events-none"
                disabled={option.disabled}
              />
            </div>
          )}
          {/* ラベル: リーフノードはチェックトグル、中間階層は階層移動 */}
          <Button
            type="button"
            variant="ghost"
            disabled={option.disabled}
            onClick={hasChildren ? onNavigate : showCheckbox ? onCheckboxToggle : undefined}
            className="flex h-auto min-w-0 flex-1 items-center justify-between p-0 text-left font-normal hover:bg-transparent focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Text truncate={true} className={cn(showCheckbox && 'ml-2')}>
              {option.label}
            </Text>
            {hasChildren && <ChevronRightIcon className="ml-2 h-4 w-4 shrink-0" />}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="ghost"
          disabled={option.disabled}
          onClick={onNavigate}
          className="flex h-auto w-full items-center justify-between p-0 text-left font-normal hover:bg-transparent focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <Text truncate={true}>{option.label}</Text>
          {option.children && option.children.length > 0 && (
            <ChevronRightIcon className="ml-2 h-4 w-4 shrink-0" />
          )}
        </Button>
      )}
    </div>
  );

  return (
    <Tooltip
      tooltipContent={option.disabledReason}
      enabled={Boolean(option.disabled && option.disabledReason)}
      side="right"
    >
      {content}
    </Tooltip>
  );
};
